import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { Globe2, MapPin, Briefcase } from 'lucide-react';

export default function ProfileSettings() {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    firstName: 'Sarah',
    lastName: 'Mitchell',
    username: 'TechMaster486',
    email: 'sarah@gocity.me',
    bio: 'Senior Software Engineer passionate about AI and machine learning. Building the future of technology through innovative solutions.',
    company: 'TechCorp',
    position: 'Senior Software Engineer',
    location: {
      continent: 'Europe',
      country: 'United Kingdom',
      city: 'London'
    },
    languages: ['English', 'Spanish'],
    expertise: ['AI', 'Machine Learning', 'Cloud Computing'],
    website: 'https://sarah.dev'
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Profile Picture */}
      <div>
        <label className={`block text-sm font-medium mb-2 ${
          theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
        }`}>
          Profile Picture
        </label>
        <div className="flex items-center space-x-4">
          <img
            src={user?.avatar}
            alt="Profile"
            className="h-20 w-20 rounded-xl object-cover"
          />
          <button
            type="button"
            className={`
              px-4 py-2 rounded-lg transition-colors
              ${theme === 'dark'
                ? 'bg-gray-700 text-gray-100 hover:bg-gray-600'
                : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
              }
            `}
          >
            Change Photo
          </button>
        </div>
      </div>

      {/* Basic Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className={`block text-sm font-medium mb-2 ${
            theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
          }`}>
            First Name
          </label>
          <input
            type="text"
            value={formData.firstName}
            onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
            className={`
              w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
              ${theme === 'dark'
                ? 'bg-gray-700 border-gray-600 text-gray-100'
                : 'bg-white border-gray-300 text-gray-900'
              }
            `}
          />
        </div>

        <div>
          <label className={`block text-sm font-medium mb-2 ${
            theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
          }`}>
            Last Name
          </label>
          <input
            type="text"
            value={formData.lastName}
            onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
            className={`
              w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
              ${theme === 'dark'
                ? 'bg-gray-700 border-gray-600 text-gray-100'
                : 'bg-white border-gray-300 text-gray-900'
              }
            `}
          />
        </div>

        <div>
          <label className={`block text-sm font-medium mb-2 ${
            theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
          }`}>
            Username
          </label>
          <input
            type="text"
            value={formData.username}
            onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
            className={`
              w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
              ${theme === 'dark'
                ? 'bg-gray-700 border-gray-600 text-gray-100'
                : 'bg-white border-gray-300 text-gray-900'
              }
            `}
          />
        </div>

        <div>
          <label className={`block text-sm font-medium mb-2 ${
            theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
          }`}>
            Email
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            className={`
              w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
              ${theme === 'dark'
                ? 'bg-gray-700 border-gray-600 text-gray-100'
                : 'bg-white border-gray-300 text-gray-900'
              }
            `}
          />
        </div>
      </div>

      {/* Bio */}
      <div>
        <label className={`block text-sm font-medium mb-2 ${
          theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
        }`}>
          Bio
        </label>
        <textarea
          value={formData.bio}
          onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
          rows={4}
          className={`
            w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
            ${theme === 'dark'
              ? 'bg-gray-700 border-gray-600 text-gray-100'
              : 'bg-white border-gray-300 text-gray-900'
            }
          `}
        />
      </div>

      {/* Professional Info */}
      <div className="space-y-4">
        <h3 className={`text-lg font-medium ${
          theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
        }`}>
          Professional Information
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
            }`}>
              Company
            </label>
            <div className="relative">
              <Briefcase className={`absolute left-3 top-2.5 h-5 w-5 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`} />
              <input
                type="text"
                value={formData.company}
                onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                className={`
                  w-full pl-10 pr-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                  ${theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-gray-100'
                    : 'bg-white border-gray-300 text-gray-900'
                  }
                `}
              />
            </div>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${
              theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
            }`}>
              Position
            </label>
            <input
              type="text"
              value={formData.position}
              onChange={(e) => setFormData(prev => ({ ...prev, position: e.target.value }))}
              className={`
                w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                ${theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-gray-100'
                  : 'bg-white border-gray-300 text-gray-900'
                }
              `}
            />
          </div>
        </div>
      </div>

      {/* Location */}
      <div className="space-y-4">
        <h3 className={`text-lg font-medium ${
          theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
        }`}>
          Location
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
            }`}>
              Continent
            </label>
            <div className="relative">
              <Globe2 className={`absolute left-3 top-2.5 h-5 w-5 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`} />
              <select
                value={formData.location.continent}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  location: { ...prev.location, continent: e.target.value }
                }))}
                className={`
                  w-full pl-10 pr-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                  ${theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-gray-100'
                    : 'bg-white border-gray-300 text-gray-900'
                  }
                `}
              >
                <option>Europe</option>
                <option>North America</option>
                <option>South America</option>
                <option>Asia</option>
                <option>Africa</option>
                <option>Oceania</option>
              </select>
            </div>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${
              theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
            }`}>
              Country
            </label>
            <div className="relative">
              <MapPin className={`absolute left-3 top-2.5 h-5 w-5 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`} />
              <input
                type="text"
                value={formData.location.country}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  location: { ...prev.location, country: e.target.value }
                }))}
                className={`
                  w-full pl-10 pr-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                  ${theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-gray-100'
                    : 'bg-white border-gray-300 text-gray-900'
                  }
                `}
              />
            </div>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${
              theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
            }`}>
              City
            </label>
            <input
              type="text"
              value={formData.location.city}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                location: { ...prev.location, city: e.target.value }
              }))}
              className={`
                w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                ${theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-gray-100'
                  : 'bg-white border-gray-300 text-gray-900'
                }
              `}
            />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end space-x-4">
        <button
          type="button"
          className={`
            px-4 py-2 rounded-lg transition-colors
            ${theme === 'dark'
              ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }
          `}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Save Changes
        </button>
      </div>
    </form>
  );
}